import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/Button';
import { authApi } from '../../services/authApi';
import { EmailVerificationResponse } from '../../types';

interface EmailVerificationFormProps {
  email: string;
  onVerificationSuccess: () => void;
  onBack: () => void;
}

export const EmailVerificationForm: React.FC<EmailVerificationFormProps> = ({
  email,
  onVerificationSuccess,
  onBack
}) => {
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [canResend, setCanResend] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer
  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (expiresAt) {
      setCanResend(true);
    }
  }, [timeRemaining, expiresAt]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const checkVerificationStatus = useCallback(async () => {
    try {
      const status = await authApi.checkEmailVerification(email);
      if (status.isVerified) {
        onVerificationSuccess();
        return;
      }
      
      if (status.hasPendingVerification && status.expiresAt) {
        const expiryDate = new Date(status.expiresAt);
        setExpiresAt(expiryDate);
        const remaining = Math.max(0, Math.floor((expiryDate.getTime() - Date.now()) / 1000));
        setTimeRemaining(remaining);
        setRemainingAttempts(status.remainingAttempts);
        setCanResend(remaining === 0);
      } else {
        setCanResend(true);
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      setCanResend(true);
    }
  }, [email, onVerificationSuccess]);

  // Initialize verification status check
  useEffect(() => {
    checkVerificationStatus();
  }, [checkVerificationStatus]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newOtpCode = [...otpCode];
    newOtpCode[index] = value.toUpperCase();
    setOtpCode(newOtpCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newOtpCode.every(digit => digit !== '') && value) {
      handleVerifyEmail(newOtpCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtpCode = [...otpCode];
    
    for (let i = 0; i < pastedText.length && i < 6; i++) {
      newOtpCode[i] = pastedText[i];
    }
    
    setOtpCode(newOtpCode);
    
    // Focus next empty field or last field
    const nextEmptyIndex = newOtpCode.findIndex(digit => digit === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();

    // Auto-submit if complete
    if (newOtpCode.every(digit => digit !== '')) {
      handleVerifyEmail(newOtpCode.join(''));
    }
  };

  const handleVerifyEmail = async (code?: string) => {
    const codeToVerify = code || otpCode.join('');
    
    if (codeToVerify.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response: EmailVerificationResponse = await authApi.verifyEmail({
        email,
        otpCode: codeToVerify
      });

      if (response.success) {
        setMessage('Email verified successfully!');
        setTimeout(onVerificationSuccess, 1500);
      } else {
        setError(response.message);
        setRemainingAttempts(response.remainingAttempts || null);
        
        // Clear the OTP inputs on error
        setOtpCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Verification failed');
      setOtpCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend || resendCooldown > 0) return;

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response: EmailVerificationResponse = await authApi.resendVerificationEmail({ email });
      
      if (response.success) {
        setMessage('Verification code sent! Check your email.');
        if (response.expiresAt) {
          const expiryDate = new Date(response.expiresAt);
          setExpiresAt(expiryDate);
          const remaining = Math.max(0, Math.floor((expiryDate.getTime() - Date.now()) / 1000));
          setTimeRemaining(remaining);
        }
        setCanResend(false);
        setResendCooldown(120); // 2 minute cooldown
        setOtpCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Verify Your Email
        </h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          We've sent a 6-digit verification code to
        </p>
        <p className="font-medium text-gray-900 dark:text-white">
          {email}
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-red-800">{error}</p>
          </div>
          {remainingAttempts !== null && remainingAttempts > 0 && (
            <p className="mt-2 text-sm text-red-700">
              {remainingAttempts} attempts remaining
            </p>
          )}
        </div>
      )}

      {message && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-green-800">{message}</p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Enter verification code
          </label>
          <div className="flex justify-center space-x-3">
            {otpCode.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-12 text-center text-lg font-bold border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                disabled={isLoading}
              />
            ))}
          </div>
        </div>

        {timeRemaining > 0 && (
          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            Code expires in {formatTime(timeRemaining)}
          </div>
        )}

        <Button
          onClick={() => handleVerifyEmail()}
          disabled={isLoading || otpCode.some(digit => digit === '')}
          isLoading={isLoading}
          className="w-full"
        >
          {isLoading ? 'Verifying...' : 'Verify Email'}
        </Button>

        <div className="text-center space-y-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Didn't receive the code?
          </p>
          <Button
            variant="ghost"
            onClick={handleResendCode}
            disabled={!canResend || resendCooldown > 0}
            className="text-blue-600 hover:text-blue-700"
          >
            {resendCooldown > 0 
              ? `Resend in ${resendCooldown}s` 
              : canResend 
                ? 'Resend Code' 
                : 'Resend Code'
            }
          </Button>
        </div>

        <Button
          variant="ghost"
          onClick={onBack}
          className="w-full mt-4"
          disabled={isLoading}
        >
          Back to Registration
        </Button>
      </div>
    </div>
  );
};
